import React, { useRef, useState, useEffect } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Upload, RefreshCw, CheckCircle2, AlertTriangle, RotateCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Mobile-optimierte Beleg-Erfassung.
 * Funktioniert auf jedem Browser (iPhone Safari, Android Chrome, Desktop) ohne App-Installation.
 * - capture="environment" → öffnet die Rückkamera direkt
 * - Live-Vorschau, Drehung, Re-Take
 * - Status-Anzeige nach Upload (qualitätsbasierte Reklamation)
 */
export default function MobileReceiptScan() {
  const fileInputRef = useRef(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const id = localStorage.getItem("demoCompanyId");
    setCompanyId(id);
    apiClient
      .get("/my/companies")
      .then((r) => {
        const c = r.data.find((x) => x._id === id) || r.data[0];
        if (c) {
          setCompanyId(c._id);
          setCompanyName(c.name || "");
          if (!id) localStorage.setItem("demoCompanyId", c._id);
        }
      })
      .catch(() => {});
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB).");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setRotation(0);
    setResult(null);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRotation(0);
  };

  const upload = async () => {
    if (!file || !companyId) return;
    setUploading(true);
    setResult(null);
    try {
      // Iter74: Erst Vision-LLM-Preview-Analyse (Iter68a), damit die echten Werte
      // (Betrag/Datum/MWST) im Receipt-POST stehen statt 0/heute.
      let amount = "0";
      let currency = "CHF";
      let dateIso = new Date().toISOString().slice(0, 10);
      let description = `Mobile Scan ${dateIso}`;
      let taxRate = "0.081";

      try {
        const preFd = new FormData();
        preFd.append("file", file);
        const pre = await apiClient.post(
          `/companies/${companyId}/receipts/analyze-preview`,
          preFd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        if (pre.data?.success) {
          if (pre.data.amount != null) amount = String(pre.data.amount);
          if (pre.data.currency) currency = pre.data.currency;
          if (pre.data.date) dateIso = pre.data.date;
          if (pre.data.description) description = pre.data.description;
          if (pre.data.tax_rate != null) taxRate = String(pre.data.tax_rate);
          if (pre.data.vendor) {
            description = `${pre.data.vendor}${description ? ` · ${description}` : ""}`;
          }
          toast.success(
            `✨ Erkannt: ${currency} ${Number(amount).toFixed(2)}`
            + (pre.data.vendor ? ` · ${pre.data.vendor}` : "")
          );
        }
      } catch (e) {
        // Fallback: ohne Pre-Analyse weiter wie vorher
      }

      const fd = new FormData();
      fd.append("amount", amount);
      fd.append("currency", currency);
      fd.append("date", dateIso);
      fd.append("description", description);
      fd.append("tax_rate", taxRate);
      fd.append("file", file);

      const r = await apiClient.post(`/companies/${companyId}/receipts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.quality_status === "low_quality") {
        toast.warning("Foto schwer lesbar — bitte neu fotografieren.");
      } else if (r.data.quality_status === "missing_amount") {
        toast.warning("Betrag nicht erkannt — bitte ergänzen.");
      } else {
        toast.success("Beleg hochgeladen & analysiert.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="mobile-scan-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Beleg scannen</h1>
          {companyName && (
            <p className="text-xs text-slate-500 mt-1">
              Firma: <span className="font-medium">{companyName}</span>
            </p>
          )}
        </div>

        {!previewUrl && (
          <Card className="p-6 text-center border-2 border-dashed border-slate-300 bg-white" data-testid="mobile-scan-empty">
            <Camera className="h-10 w-10 mx-auto text-slate-400" />
            <p className="text-sm text-slate-600 mt-3">
              Tippen Sie unten auf <span className="font-semibold">"Foto aufnehmen"</span>, um die Kamera zu öffnen.
            </p>
            <p className="text-[11px] text-slate-500 mt-2">
              Tipp: Beleg flach auflegen, gerade halten, gute Beleuchtung.
            </p>
          </Card>
        )}

        {previewUrl && (
          <Card className="p-2 bg-white border border-slate-200 overflow-hidden" data-testid="mobile-scan-preview-card">
            <div className="relative bg-slate-900 rounded overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh" }}>
              <img
                src={previewUrl}
                alt="Beleg-Vorschau"
                style={{ transform: `rotate(${rotation}deg)`, maxHeight: "70vh", maxWidth: "100%" }}
                className="object-contain transition-transform"
                data-testid="mobile-scan-preview-img"
              />
              <button
                type="button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-white/90 text-slate-900 shadow-lg flex items-center justify-center"
                data-testid="mobile-scan-rotate-button"
              >
                <RotateCw className="h-5 w-5" />
              </button>
            </div>
          </Card>
        )}

        {result && (
          <Card
            className={`p-4 border-2 ${
              result.quality_status === "low_quality" || result.quality_status === "missing_amount"
                ? "border-amber-300 bg-amber-50"
                : "border-emerald-300 bg-emerald-50"
            }`}
            data-testid="mobile-scan-result"
          >
            <div className="flex items-start gap-2">
              {result.quality_status === "low_quality" || result.quality_status === "missing_amount" ? (
                <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 text-sm space-y-1">
                {result.quality_message && (
                  <div className="font-medium text-slate-900" data-testid="mobile-scan-quality-msg">
                    {result.quality_message}
                  </div>
                )}
                <div className="text-slate-700">
                  <strong>{result.merchant_name_ai || result.vendor_raw || "Beleg"}</strong>{" "}
                  · {formatDate(result.date)}
                </div>
                <div className="text-slate-700">
                  Brutto: <span className="font-mono">{Number(result.amount || 0).toFixed(2)} {result.currency}</span>
                  {result.subtotal && (
                    <> · Netto: <span className="font-mono">{Number(result.subtotal).toFixed(2)}</span></>
                  )}
                  {result.tax_rate && (
                    <> · MwSt: <span className="font-mono">{result.tax_rate}%</span></>
                  )}
                </div>
                {result.ai_suggestion?.suggested_account_id && (
                  <div className="text-xs text-slate-600 mt-1">
                    Vorschlag Konto: <span className="font-mono">{result.ai_suggestion.suggested_account_id}</span>
                    {result.ai_suggestion.comment && <> · {result.ai_suggestion.comment}</>}
                  </div>
                )}
                {result.vendor_match?.auto_match && (
                  <div
                    className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                    data-testid="mobile-scan-vendor-matched"
                  >
                    ✓ Lieferant erkannt: <strong>{result.vendor_match.auto_match.name}</strong>
                  </div>
                )}
                {!result.vendor_match?.auto_match && result.vendor_match?.vendor_name && (
                  <div
                    className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                    data-testid="mobile-scan-vendor-not-found"
                  >
                    ❓ Lieferant nicht im System — bitte in der Web-App anlegen.
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Bottom action bar */}
        <div className="sticky bottom-2 flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="mobile-scan-file-input"
          />
          {!previewUrl ? (
            <Button
              type="button"
              className="flex-1 h-14 text-base rounded-full bg-slate-900 text-white shadow-lg"
              onClick={() => fileInputRef.current?.click()}
              data-testid="mobile-scan-take-photo-button"
            >
              <Camera className="h-5 w-5 mr-2" /> Foto aufnehmen
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-14 text-base rounded-full bg-white"
                onClick={reset}
                disabled={uploading}
                data-testid="mobile-scan-retake-button"
              >
                <RefreshCw className="h-5 w-5 mr-2" /> Neu
              </Button>
              <Button
                type="button"
                className="flex-1 h-14 text-base rounded-full bg-emerald-600 text-white shadow-lg"
                onClick={upload}
                disabled={uploading}
                data-testid="mobile-scan-upload-button"
              >
                <Upload className="h-5 w-5 mr-2" />
                {uploading ? "Sende …" : "Hochladen"}
              </Button>
            </>
          )}
        </div>

        <div className="text-center pt-2">
          <a href="/receipts" className="text-xs text-slate-500 underline" data-testid="mobile-scan-link-list">
            Zur vollen Belege-Liste
          </a>
        </div>
      </div>
    </div>
  );
}
